import React, { useState, useEffect } from 'react';
import { 
  Sparkles, TrendingUp, Users, Target, Award, AlertTriangle, 
  BarChart3, Activity, Zap, RefreshCw, X, ChevronRight, Database,
  TrendingDown, Medal, Star, Flame, Trophy, MessageSquare, Send, Loader,
  Table as TableIcon
} from 'lucide-react';
import {
  BarChart, Bar, ScatterChart, Scatter, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import { getCachedInsightsData, cacheInsightsData } from '../../utils/bihar-cache';

export default function BiharAIInsightsEnhanced() {
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState('sample');
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [selectedModal, setSelectedModal] = useState(null);
  
  // AI Query state
  const [query, setQuery] = useState('');
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryResult, setQueryResult] = useState(null);
  const [queryError, setQueryError] = useState(null);

  useEffect(() => {
    loadDatasets();
  }, []);

  useEffect(() => {
    if (selectedDataset) {
      loadInsights(selectedDataset);
    }
  }, [selectedDataset]);

  const loadDatasets = async () => {
    try {
      const response = await fetch('/api/bihar-datasets');
      const data = await response.json();
      setDatasets(data.datasets || []);
    } catch (error) {
      console.error('Error loading datasets:', error);
    }
  };

  const loadInsights = async (datasetId) => {
    setLoading(true);
    try {
      // Check cache first
      const cached = getCachedInsightsData(datasetId);
      if (cached) {
        setInsights(cached);
        setLoading(false);
        return;
      }

      // Cache miss - fetch from API
      const response = await fetch(`/api/bihar-insights?dataset_id=${datasetId}`);
      const data = await response.json();
      setInsights(data);
      
      // Cache the result
      cacheInsightsData(datasetId, data);
    } catch (error) {
      console.error('Error loading insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

  const metricLabels = {
    height_cm: 'Height',
    weight_kg: 'Weight',
    vertical_jump_cm: 'Vertical Jump',
    broad_jump_cm: 'Broad Jump',
    sprint_30m_sec: '30m Sprint',
    run_800m_sec: '800m Run',
    shuttle_run_sec: 'Shuttle Run',
    football_throw_m: 'Football Throw'
  };

  const openAthleteListModal = (title, athletes, description) => {
    setSelectedModal({ title, athletes, description });
  };

  const handleQuery = async () => {
    if (!query.trim() || !selectedDataset) return;

    setQueryLoading(true);
    setQueryError(null);
    setQueryResult(null);

    try {
      const response = await fetch('/api/bihar-ai-query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: query.trim(),
          datasetId: selectedDataset
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Query failed');
      }

      setQueryResult(data);
    } catch (err) {
      console.error('Query error:', err);
      setQueryError(err.message);
    } finally {
      setQueryLoading(false);
    }
  };

  const quickQueries = [
    'Show top 5 athletes by height',
    'Athletes with vertical jump over 50cm',
    'Find athletes with 800m run time under 4:30',
    'Show athletes with weight between 40-60kg',
    'Top 3 performers in broad jump',
    'Bottom 5 athletes in 30m sprint'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-3" />
          <p className="text-slate-400">Loading insights...</p>
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-12 text-center">
        <Database className="w-12 h-12 text-slate-600 mx-auto mb-3" />
        <p className="text-slate-400">No insights available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Dataset Selector */}
      {datasets.length > 1 && (
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
          <select
            value={selectedDataset || ''}
            onChange={(e) => setSelectedDataset(e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {datasets.map(dataset => (
              <option key={dataset.id} value={dataset.id}>
                {dataset.name} ({dataset.record_count} records)
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Main 60-40 Split Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Panel (60%) - AI Query Assistant */}
        <div className="lg:col-span-3 space-y-6">
          {/* AI Query Assistant Section */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-purple-600/20 rounded-lg">
            <MessageSquare className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">AI Query Assistant</h2>
            <p className="text-slate-400">Ask questions in natural language to filter and analyze athlete data</p>
          </div>
        </div>

        {/* Quick Queries */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-slate-400 mb-3">Quick Queries</h3>
          <div className="flex flex-wrap gap-2">
            {quickQueries.map((q, idx) => (
              <button
                key={idx}
                onClick={() => setQuery(q)}
                className="px-3 py-2 bg-slate-700/50 hover:bg-slate-700 text-slate-300 text-sm rounded-lg transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        </div>

        {/* Query Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-300 mb-2">Ask a Question</label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleQuery()}
              placeholder="e.g., Show athletes with height above 160cm and weight below 50kg"
              className="flex-1 px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
              disabled={queryLoading}
            />
            <button
              onClick={handleQuery}
              disabled={queryLoading || !query.trim() || !selectedDataset}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {queryLoading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>Query</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Error Display */}
        {queryError && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4">
            <div className="flex items-start space-x-3">
              <X className="w-5 h-5 text-red-400 mt-0.5" />
              <div>
                <h4 className="text-red-400 font-medium mb-1">Query Error</h4>
                <p className="text-red-300 text-sm">{queryError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Query Results */}
        {queryResult && (
          <div className="space-y-4">
            {/* Query Info */}
            <div className="bg-slate-900/50 rounded-xl border border-slate-700 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Database className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-medium text-slate-300">Query Results</span>
                </div>
                <span className="text-xs text-slate-400">
                  {queryResult.athletes?.length || 0} athletes found
                </span>
              </div>
              {queryResult.interpretation && (
                <p className="text-sm text-slate-400 mb-3">{queryResult.interpretation}</p>
              )}
              {queryResult.filters && Object.keys(queryResult.filters).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(queryResult.filters).map(([key, value]) => (
                    <span key={key} className="px-2 py-1 bg-purple-500/20 border border-purple-500/30 text-purple-300 text-xs rounded">
                      {key}: {typeof value === 'object' ? `${value.min}-${value.max}` : value}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Data Table */}
            {queryResult.athletes && queryResult.athletes.length > 0 && (
              <div className="bg-slate-900/50 rounded-xl border border-slate-700 overflow-hidden">
                <div className="p-4 border-b border-slate-700 flex items-center space-x-2">
                  <TableIcon className="w-4 h-4 text-slate-400" />
                  <h3 className="text-sm font-medium text-slate-300">Athlete Data</h3>
                </div>
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full">
                    <thead className="bg-slate-900/50 border-b border-slate-700 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Height (cm)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Weight (kg)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">V. Jump (cm)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Broad Jump (cm)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">30m Sprint (s)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">800m Run</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                      {queryResult.athletes.map((athlete, idx) => (
                        <tr key={idx} className="hover:bg-slate-700/30 transition-colors">
                          <td className="px-4 py-3 text-sm text-white font-medium">{athlete.full_name || '-'}</td>
                          <td className="px-4 py-3 text-sm text-slate-300">{athlete.height_cm?.toFixed(1) || '-'}</td>
                          <td className="px-4 py-3 text-sm text-slate-300">{athlete.weight_kg?.toFixed(1) || '-'}</td>
                          <td className="px-4 py-3 text-sm text-slate-300">{athlete.vertical_jump_cm?.toFixed(1) || '-'}</td>
                          <td className="px-4 py-3 text-sm text-slate-300">{athlete.broad_jump_cm?.toFixed(1) || '-'}</td>
                          <td className="px-4 py-3 text-sm text-slate-300">{athlete.sprint_30m_sec?.toFixed(2) || '-'}</td>
                          <td className="px-4 py-3 text-sm text-slate-300">
                            {athlete.run_800m_sec ? (() => {
                              const hours = Math.floor(athlete.run_800m_sec / 3600);
                              const mins = Math.floor((athlete.run_800m_sec % 3600) / 60);
                              const secs = Math.floor(athlete.run_800m_sec % 60);
                              const ms = Math.round((athlete.run_800m_sec % 1) * 100);
                              return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}:${ms.toString().padStart(2, '0')}`;
                            })() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* No Results */}
            {queryResult.athletes && queryResult.athletes.length === 0 && (
              <div className="bg-slate-900/50 rounded-xl border border-slate-700 p-12 text-center">
                <Database className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No athletes match your query criteria</p>
              </div>
            )}
          </div>
        )}
          </div>
        </div>

        {/* Right Panel (40%) - Metrics & Charts */}
        <div className="lg:col-span-2 space-y-6">
          {/* Key Metrics Cards */}
          <div className="space-y-3">
            <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 border border-blue-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-6 h-6 text-blue-400" />
                <span className="text-2xl font-bold text-white">{insights.athleteCount}</span>
              </div>
              <p className="text-blue-300 text-sm font-medium">Total Athletes</p>
            </div>
            <div 
              className="bg-gradient-to-br from-green-600/20 to-green-800/20 border border-green-500/30 rounded-xl p-4 cursor-pointer hover:scale-105 transition-transform"
              onClick={() => openAthleteListModal('Elite Athletes', insights.performanceTiers.elite.athletes, 'Top 10% performers across all metrics')}
            >
              <div className="flex items-center justify-between mb-2">
                <Trophy className="w-6 h-6 text-green-400" />
                <span className="text-2xl font-bold text-white">{insights.performanceTiers.elite.count}</span>
              </div>
              <p className="text-green-300 text-sm font-medium">Elite Athletes</p>
              <p className="text-green-400/60 text-xs mt-1">Click to view list</p>
            </div>
            <div 
              className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 border border-purple-500/30 rounded-xl p-4 cursor-pointer hover:scale-105 transition-transform"
              onClick={() => openAthleteListModal('High Potential Athletes', insights.improvementOpportunities, 'Athletes with strong overall performance but specific areas for improvement')}
            >
              <div className="flex items-center justify-between mb-2">
                <Star className="w-6 h-6 text-purple-400" />
                <span className="text-2xl font-bold text-white">{insights.improvementOpportunities.length}</span>
              </div>
              <p className="text-purple-300 text-sm font-medium">High Potential</p>
              <p className="text-purple-400/60 text-xs mt-1">Click to view list</p>
            </div>
            <div className="bg-gradient-to-br from-orange-600/20 to-orange-800/20 border border-orange-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Activity className="w-6 h-6 text-orange-400" />
                <span className="text-2xl font-bold text-white">{insights.correlations.length}</span>
              </div>
              <p className="text-orange-300 text-sm font-medium">Correlations Found</p>
            </div>
          </div>

          {/* Top Performers by Metric */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Medal className="w-5 h-5 text-yellow-400" />
          <h3 className="text-lg font-semibold text-white">Top Performers by Metric</h3>
        </div>
        <p className="text-sm text-slate-400 mb-4">Leading athletes in each performance category. Click to view full lists.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(insights.topPerformers).map(([metric, athletes]) => (
            <div 
              key={metric}
              className="bg-slate-700/30 rounded-lg p-4 cursor-pointer hover:bg-slate-700/50 transition-colors"
              onClick={() => openAthleteListModal(`Top ${metricLabels[metric]} Performers`, athletes, `Athletes with the best ${metricLabels[metric]} performance`)}
            >
              <h4 className="text-sm font-semibold text-white mb-2">{metricLabels[metric]}</h4>
              <div className="space-y-1">
                {athletes.slice(0, 3).map((athlete, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs">
                    <span className="text-slate-300 truncate">{athlete.name}</span>
                    <span className="text-blue-400 ml-2">{athlete.value?.toFixed(1)}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-end mt-2 text-xs text-purple-400">
                <span>View all</span>
                <ChevronRight className="w-3 h-3 ml-1" />
              </div>
            </div>
          ))}
        </div>
      </div>

          {/* Percentile Distribution */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <div className="flex items-center space-x-2 mb-4">
          <BarChart3 className="w-5 h-5 text-green-400" />
          <h3 className="text-lg font-semibold text-white">Overall Percentile Distribution</h3>
        </div>
        <p className="text-sm text-slate-400 mb-4">Distribution of athletes across percentile ranges</p>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={insights.percentileDistribution}>
            <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
            <XAxis dataKey="range" stroke="#94a3b8" />
            <YAxis stroke="#94a3b8" />
            <Tooltip />
            <Bar dataKey="count" fill="#3b82f6" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Athlete List Modal */}
      {selectedModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">{selectedModal.title}</h3>
                <p className="text-sm text-slate-400 mt-1">{selectedModal.description}</p>
              </div>
              <button
                onClick={() => setSelectedModal(null)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-2">
                {selectedModal.athletes.map((athlete, idx) => (
                  <div key={idx} className="bg-slate-700/30 rounded-lg p-4 flex items-center justify-between hover:bg-slate-700/50 transition-colors">
                    <div className="flex items-center space-x-4">
                      <span className="text-slate-400 font-mono text-sm w-8">{idx + 1}</span>
                      <div>
                        <p className="text-white font-medium">{athlete.name || 'Unknown'}</p>
                        {athlete.id && <p className="text-xs text-slate-400">ID: {athlete.id}</p>}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {athlete.avgPercentile != null && (
                        <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">
                          {athlete.avgPercentile}th percentile
                        </span>
                      )}
                      {athlete.score != null && (
                        <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                          Score: {athlete.score}
                        </span>
                      )}
                      {athlete.value != null && (
                        <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm">
                          {athlete.value.toFixed(2)}
                        </span>
                      )}
                      {athlete.percentile != null && (
                        <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm">
                          {athlete.percentile}th %ile
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
